import { useCallback, useEffect, useState } from 'react';
import { DeviceARPDataType } from '../types/buildingTypes';
import { formatMacAddress } from '../utils/macFormatter';
import { findDuplicateDescriptions } from '../utils/dhcpReservationValidation';

export const ALLOWED_DESCRIPTION_CHARACTERS: RegExp = /^[a-zA-Z0-9-_]*$/;
export const MAX_DESCRIPTION_LENGTH = 20;

export type BulkDhcpReservationRowEntry = {
  row: DeviceARPDataType;
  description: string;
  touched: boolean;
};

export type UseBulkDhcpReservationFormResult = {
  entries: BulkDhcpReservationRowEntry[];
  setDescription: (ipAddress: string, value: string) => void;
  markTouched: (ipAddress: string) => void;
  isFormValid: boolean;
  duplicateDescriptions: string[];
  allowedInputCharacters: RegExp;
  maxDescriptionLength: number;
};

export const isDescriptionValid = (description: string): boolean =>
  description.trim().length > 0 && ALLOWED_DESCRIPTION_CHARACTERS.test(description);

/**
 * Manages one independently-editable description field per selected ARP
 * row (unlike Port Flip's single shared field), seeding each from the
 * row's MAC address exactly like the single-reservation `AddDhcpReservationForm`.
 */
function useBulkDhcpReservationForm(
  selectedRows: DeviceARPDataType[]
): UseBulkDhcpReservationFormResult {
  const [entries, setEntries] = useState<BulkDhcpReservationRowEntry[]>([]);

  useEffect(() => {
    setEntries(
      selectedRows.map((row) => ({
        row,
        description: formatMacAddress(row.mac_address),
        touched: false,
      }))
    );
  }, [selectedRows]);

  const setDescription = useCallback((ipAddress: string, value: string) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.row.ip_address === ipAddress ? { ...entry, description: value } : entry
      )
    );
  }, []);

  const markTouched = useCallback((ipAddress: string) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.row.ip_address === ipAddress ? { ...entry, touched: true } : entry
      )
    );
  }, []);

  const duplicateDescriptions = findDuplicateDescriptions(entries);

  const isFormValid =
    entries.length > 0 &&
    entries.every((entry) => isDescriptionValid(entry.description)) &&
    duplicateDescriptions.length === 0;

  return {
    entries,
    setDescription,
    markTouched,
    isFormValid,
    duplicateDescriptions,
    allowedInputCharacters: ALLOWED_DESCRIPTION_CHARACTERS,
    maxDescriptionLength: MAX_DESCRIPTION_LENGTH,
  };
}

export default useBulkDhcpReservationForm;
